import './parcelform.css';
import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Col, Row } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ServiceFactory } from '../../Api/Services/ServiceFactory';
import { useToast } from '../../components/ToastContext/ToastContext';
import { ServiceNames } from '../../Api/registry/Services';
import { fetchPerson } from '../../store/slices/personSlice';
import FarmSelector from '../FarmSelector/FarmSelector';
import SoilTexture from '../SoilTexture/SoilTexture';

const ORGANIC_MATTER_OPTIONS = ['Low', 'Moderate', 'High', 'Very High'];
let TEXTURE_OPTIONS = ['Clay', 'Silty Clay', 'Sandy Clay', 'Clay Loam', 'Silty Clay Loam',
  'Sandy Clay Loam', 'Loam', 'Silty Loam', 'Sandy Loam', 'Silt', 'Loamy Sand',
  'Sand'];

const initialFormState = {
  name: '',
  description: '',
  farmId: '',
  area: '',
  position: {
    type: 'manual',
    latitude: '',
    longitude: '',
    roiId: ''
  },
  ph: '',
  organicMatter: 'Moderate',
  texture: '',
  clayPercentage: '',
  siltPercentage: '',
  sandPercentage: ''
};

const ParcelForm = ({ show, onHide, parcel, mode }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const dispatch = useDispatch();

  const person = useSelector(state => state.person?.person);
  const farms = person?.agriFarms || [];

  const [formData, setFormData] = useState(initialFormState);
  const [value, setValue] = useState(false);
  const [checked, setChecked] = useState(false);

  const handleChange = (value, checked) => {
    setChecked(!checked);
    setValue(!value);
  }

  useEffect(() => {
    if (show) {
      if (mode === 'update' && parcel) {
        const { clayPercentage, siltPercentage, sandPercentage } = parcel;

        setFormData({
          name: parcel.name || '',
          description: parcel.description || '',
          farmId: parcel.farmReference?.id || '',
          area: parcel.area || '',
          position: {
            type: 'manual',
            latitude: parcel.latitude || '',
            longitude: parcel.longitude || '',
            roiId: parcel.roiReference || ''
          },
          ph: parcel.ph || '',
          organicMatter: parcel.organicMatter || 'Moderate',
          texture: parcel.texture || '',
          clayPercentage: clayPercentage || '',
          siltPercentage: siltPercentage || '',
          sandPercentage: sandPercentage || ''
        });

        if (clayPercentage && siltPercentage && sandPercentage) {
          setValue(true);
          setChecked(true);
        } else {
          setValue(false);
          setChecked(false);
        }
      } else {
        setFormData(initialFormState);
        setValue(false);
        setChecked(false);
      }
    }
  }, [show, mode, parcel]);


  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSoilChange = (field, value) => {
    if (field === 'texture') {
      if (typeof value === 'string') {
        // Viene dalla combo TEXTURE_OPTIONS
        setFormData(prev => ({
          ...prev,
          texture: value
        }));
      } else {
        // Viene dal componente SoilTexture
        setFormData(prev => ({
          ...prev,
          clayPercentage: value.clay,
          siltPercentage: value.silt,
          sandPercentage: value.sand
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handlePositionTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      position: {
        ...prev.position,
        type
      }
    }));
  };

  const handlePositionChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      position: {
        ...prev.position,
        [field]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const userService = await ServiceFactory.getService(ServiceNames.UserService);

      if (checked) {
        formData.texture = null;
      } else {
        formData.clayPercentage = null;
        formData.siltPercentage = null;
        formData.sandPercentage = null;
      }

      const request = {
        ...(mode === 'update' && parcel?.id ? { id: parcel.id } : {}),
        name: formData.name,
        description: formData.description,
        farmReference: formData.farmId,
        area: parseFloat(formData.area),
        latitude: parseFloat(formData.position.latitude),
        longitude: parseFloat(formData.position.longitude),
        roiReference: formData.position.roiId,
        soilPh: parseFloat(formData.ph),
        soilMatter: formData.organicMatter,
        soilTexture: formData.texture,
        soilClayPercentage: parseFloat(formData.clayPercentage),
        soilSiltPercentage: parseFloat(formData.siltPercentage),
        soilSandPercentage: parseFloat(formData.sandPercentage)
      };

      await userService.saveParcel(request);
      await dispatch(fetchPerson()).unwrap();
      showToast(t("common:messages.success"), "success");
      onHide();
    } catch (error) {
      showToast(error.message || t("error:wrong"), "error");
    }
  };

  const handleHide = () => {
    setFormData(initialFormState);
    setValue(false);
    setChecked(false);
    onHide();
  };

  const handleReset = () => {
    setFormData(initialFormState);
    setValue(false);
    setChecked(false);
  };

  return (
    <Modal show={show} onHide={handleHide} className="parcel-form-modal">
      <Modal.Header closeButton>
        <Modal.Title>{mode === 'update' ? t("parcelform:labels.editParcel") : t("parcelform:labels.createNewParcel")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          {/* Prima riga: Name, Description, Farm */}
          <div className="form-row">
            <Form.Group className="name-group">
              <Form.Label>{t("parcelform:labels.name")}</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="description-group">
              <Form.Label>{t("parcelform:labels.description")}</Form.Label>
              <Form.Control
                type="text"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="farm-group">
              <Form.Label>{t("parcelform:labels.farm")}</Form.Label>
              <FarmSelector
                selectedFarm={formData.farmId}
                onFarmChange={(value) => handleInputChange('farmId', value)}
                farms={farms}
              />
            </Form.Group>
          </div>

          {/* Seconda riga: Area e Position */}
          <div className="form-row">

            <div className="position-container">
              <div className="position-header">
                <Form.Label>{t("parcelform:labels.position")}</Form.Label>
                {mode !== 'update' && (
                  <div className="position-options">
                    <Form.Check
                      type="radio"
                      name="positionType"
                      id="manual"
                      label={t("parcelform:labels.manualInput")}
                      checked={formData.position.type === 'manual'}
                      onChange={() => handlePositionTypeChange('manual')}
                      inline
                    />
                    <Form.Check
                      type="radio"
                      name="positionType"
                      id="roi"
                      label={t("parcelform:labels.selectRoi")}
                      checked={formData.position.type === 'roi'}
                      onChange={() => handlePositionTypeChange('roi')}
                      inline
                    />
                  </div>
                )}
              </div>

              <div className="position-content">
                <div className="coordinates-group">
                  <div className="coordinate-field">
                    <Form.Label>{t("parcelform:labels.latitude")}</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.0001"
                      value={formData.position.latitude}
                      onChange={(e) => handlePositionChange('latitude', e.target.value)}
                      disabled={mode === 'update'} // Disabilita il campo
                    />
                  </div>
                  <div className="coordinate-field">
                    <Form.Label>{t("parcelform:labels.longitude")}</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.0001"
                      value={formData.position.longitude}
                      onChange={(e) => handlePositionChange('longitude', e.target.value)}
                      disabled={mode === 'update'} // Disabilita il campo
                    />
                  </div>
                  <Form.Group className="area-group">
                    <Form.Label>{t("parcelform:labels.area")}</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.area}
                      onChange={(e) => handleInputChange('area', e.target.value)}
                      disabled={mode === 'update'} // Disabilita il campo
                    />
                  </Form.Group>
                </div>
              </div>
            </div>
          </div>

          {/* Terza riga: Soil Properties */}
          <div className="soil-section">
            <div className="section-header">
              <span className="section-title">{t("parcelform:labels.soilProp")}</span>
              <div className="section-line"></div>
            </div>

            <div className="soil-content">
              <div className="soil-inputs">
                <div className="ph-field">
                  <Form.Label>{t("parcelform:labels.ph")}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="-9.99"
                    max="14.99"
                    value={formData.ph}
                    onChange={(e) => handleSoilChange('ph', e.target.value)}
                  />
                </div>

                <div className="select-field">
                  <Form.Label>{t("parcelform:labels.organicMatter")}</Form.Label>
                  <Form.Select
                    value={formData.organicMatter}
                    onChange={(e) => handleSoilChange('organicMatter', e.target.value)}
                  >
                    {ORGANIC_MATTER_OPTIONS.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </Form.Select>
                </div>
              </div>

              <div className="soil-texture-component">
                <Row>
                  <Col sm={5}>
                    <div className="select-field">
                      <Form.Label>{t("parcelform:labels.texture")}</Form.Label>
                      <Form.Select
                        disabled={value}
                        value={formData.texture}
                        onChange={(e) => { handleSoilChange('texture', e.target.value) }}
                      >
                        {TEXTURE_OPTIONS.map(option => (
                          <option key={option} value={option}>{(checked && value) ? '' : option}</option>
                        ))}
                      </Form.Select>
                    </div>
                  </Col>
                  <Col className='col-check-triangle' sm={7}>
                    <Form.Check
                      checked={checked}
                      type='checkbox'
                      id='use-triangle'
                      label={t("parcelform:labels.useTriangle")}
                      onChange={() => {
                        handleChange(value, checked);
                        handleSoilChange('texture', '')
                      }}
                    />
                  </Col>
                </Row>
                {value === true && (
                  <>
                    <SoilTexture
                      soilTexture={formData.texture}
                      initialComposition={{
                        clay: formData.clayPercentage,
                        silt: formData.siltPercentage,
                        sand: formData.sandPercentage
                      }}
                      onSoilChange={(texture) => handleSoilChange('texture', texture)}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
          <Modal.Footer>
            {<Button variant="secondary" onClick={() => {
              handleReset();
            }}>
              {t("parcelform:buttons.reset")}
            </Button>}
            <Button variant="primary" type="submit" onClick={() => {
            }}>
              {t("parcelform:buttons.createParcel")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ParcelForm;